import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { ListViewDataResult } from '@progress/kendo-angular-listview';
import { map, Observable } from 'rxjs';
import {
  toDataSourceRequestString,
  translateDataSourceResultGroups,
} from '@progress/kendo-data-query';
import { DistinctItemsSource, GridState } from '@shared';

@Injectable({
  providedIn: 'root',
})
export class ReportService implements DistinctItemsSource {
  private apiUrl: string = environment.apiUrl + '/reports';
  constructor(private httpClient: HttpClient) {}

  getReportData(
    reportType: string,
    state: GridState,
  ): Observable<GridDataResult> {
    const hasGroups = state.group && state.group.length;
    return this.httpClient
      .get<GridDataResult>(
        `${this.apiUrl}/${reportType}?${toDataSourceRequestString(state)}`,
        {},
      )
      .pipe(
        map(({ data, total }) => ({
          data: hasGroups ? translateDataSourceResultGroups(data) : data,
          total: total,
        })),
      );
  }

  /**
   * Values offered by the checkbox list of a column filter, read from the
   * report endpoint so the list is not limited to the rows of the current page.
   */
  getDistinctItems(
    state: GridState,
    reportType: string,
    field: string,
    restaurantId?: string,
    extraParams?: Record<string, string | string[]>,
  ): Observable<ListViewDataResult> {
    const extra = Object.entries(extraParams ?? {})
      .flatMap(([key, value]) =>
        (Array.isArray(value) ? value : [value]).map(
          (item) => `&${key}=${encodeURIComponent(item)}`,
        ),
      )
      .join('');

    return this.httpClient.get<ListViewDataResult>(
      `${this.apiUrl}/${reportType}/distinctItems/${field}?${toDataSourceRequestString(state)}${extra}`,
    );
  }

  exportExceptions(reportType: string): Observable<Blob> {
    return this.httpClient.get(`${this.apiUrl}/export/${reportType}`, {
      responseType: 'blob',
    });
  }

  getSubstitutionAnchors(restaurantId: number): Observable<GridDataResult> {
    return this.httpClient.get<GridDataResult>(
      `${this.apiUrl}/substitutions/anchors?restaurantId=${restaurantId}`
    );
  }

  fixSubstitutionExceptions(tierId: string, state: GridState): Observable<number> {
    return this.httpClient.get<number>(
      `${this.apiUrl}/substitutions/fix/${tierId}?${toDataSourceRequestString(state)}`
    );
  }
}
